// Settings card (office and admin, Session AUD, email system Phase 5, Item 5): Email deliverability.
//
// Reads ELE's sending domain's three DNS records (SPF, DKIM, DMARC) live when the person presses "Check now", and shows
// each as passed, worth improving, failed or not checked, in plain English, with the exact record found (in a block that
// can be copied, so it can be shown to whoever manages the domain's DNS) and what to do. Read-only: the platform never
// changes DNS. Nothing is checked until the button is pressed, and nothing is cached: every press reads DNS again.
// "Not checked" is never shown as a pass (a check that did not run is never an all-clear).
import { useState } from "react";
import { api } from "../../lib/api";
import type { EmailDeliverabilityCheck, EmailDeliverabilityReport, EmailDeliverabilityStatus } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { StatusPill } from "../../components/StatusPill";
import { formatDateTime } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";

const STATUS_WORD: Record<EmailDeliverabilityStatus, string> = {
  pass: "Passed",
  warn: "Could be stronger",
  fail: "Failed",
  unknown: "Not checked",
};

const BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong disabled:opacity-50";

function CheckRow({ check }: { check: EmailDeliverabilityCheck }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!check.record) return;
    try {
      await navigator.clipboard.writeText(check.record);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <li className="rounded-ele border border-ele-purple-accent p-3" aria-label={check.label}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-heading text-sm text-ele-strong">{check.label}</span>
        <StatusPill tone={statusEntry("deliverability", check.status).tone} word={STATUS_WORD[check.status]} />
      </div>
      <p className="mt-2 text-sm text-ele-body">{check.summary}</p>
      <p className="mt-2 text-xs text-ele-body">
        Looked up at <span className="font-mono [overflow-wrap:anywhere]">{check.lookedUp}</span>
      </p>
      {check.record ? (
        <div className="mt-1">
          <pre
            aria-label={`${check.key.toUpperCase()} record found`}
            className="whitespace-pre-wrap rounded-ele bg-ele-inset p-2 font-mono text-xs text-ele-strong [overflow-wrap:anywhere] select-all"
          >
            {check.record}
          </pre>
          <button type="button" className={`${BTN} mt-1`} onClick={() => void copy()}>
            {copied ? "Copied" : "Copy the record"}
          </button>
        </div>
      ) : (
        <p className="mt-1 text-xs text-ele-body">No record found there.</p>
      )}
      {check.whatToDo && (
        <p className="mt-2 text-sm text-ele-strong">
          <span className="font-semibold">What to do: </span>
          {check.whatToDo}
        </p>
      )}
    </li>
  );
}

export function EmailDeliverabilityCard() {
  const [report, setReport] = useState<EmailDeliverabilityReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function check() {
    setBusy(true);
    setError(null);
    try {
      setReport(await api.settings.emailDeliverability());
    } catch (e) {
      setError(friendlyApiError(e, "The check could not be run just now. Please try again in a moment."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Email deliverability">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg text-ele-blue">Email deliverability</h2>
        <button type="button" className={BTN} onClick={() => void check()} disabled={busy}>
          {busy ? "Checking..." : "Check now"}
        </button>
      </div>
      <p className="mb-3 text-sm text-ele-body">
        Receiving mail servers decide whether our email reaches the inbox or the spam folder partly by three settings on our
        domain: SPF, DKIM and DMARC. This reads them as the rest of the world sees them and says plainly whether each is right.
        It changes nothing. If one needs fixing, show it to whoever manages the domain&apos;s DNS.
      </p>
      {error && (
        <p role="alert" className="text-sm text-ele-strong">
          {error}
        </p>
      )}
      {!report && !error && <p className="text-sm text-ele-body">Press Check now to read the records.</p>}
      {report && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={statusEntry("deliverability", report.overall).tone} word={STATUS_WORD[report.overall]} />
            <span className="text-xs text-ele-body">Checked {formatDateTime(report.checkedAt)}</span>
          </div>
          <p className="text-sm text-ele-strong">{report.overallSummary}</p>
          {report.notes.map((n) => (
            <p key={n} className="text-xs text-ele-body">
              {n}
            </p>
          ))}
          {report.domains.map((d) => (
            <div key={d.domain}>
              <h3 className="mb-2 font-heading text-base text-ele-strong [overflow-wrap:anywhere]">{d.domain}</h3>
              <ul className="space-y-2">
                {d.checks.map((c) => (
                  <CheckRow key={c.key} check={c} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
